import { createFileRoute } from '@tanstack/react-router'
import { useMenuToggle } from '@/features/super-admin/super-admin-shell-context'
import { useState } from 'react'
import { Download, ChevronLeft, ChevronRight } from 'lucide-react'
import { SuperAdminHeader } from '@/features/super-admin/components/SuperAdminHeader'
import { useAuditLogs, useExportAuditLogs } from '@/features/super-admin/hooks/use-audit-logs'
import { AuditLogTable } from '@/features/super-admin/components/audit-logs/AuditLogTable'

export const Route = createFileRoute('/super-admin/audit-logs/')({
  component: AuditLogsPage,
})

function AuditLogsPage() {
  const { onMenuToggle } = useMenuToggle()
  const [page, setPage] = useState(1)
  const [actionFilter, setActionFilter] = useState<string>('')
  const [targetTypeFilter, setTargetTypeFilter] = useState<string>('')
  const [dateRange, setDateRange] = useState<{ start?: string; end?: string }>({})

  const { data: logsData, isLoading: logsLoading } = useAuditLogs({
    page,
    page_size: 30,
    action: actionFilter || undefined,
    target_type: targetTypeFilter || undefined,
    start_date: dateRange.start,
    end_date: dateRange.end,
  })

  const exportMutation = useExportAuditLogs()

  const handleExport = async (format: 'csv' | 'json') => {
    const blob = await exportMutation.mutateAsync({
      format,
      action: actionFilter || undefined,
      target_type: targetTypeFilter || undefined,
      start_date: dateRange.start,
      end_date: dateRange.end,
    })

    // Download the file
    const url = window.URL.createObjectURL(blob as Blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `audit-logs-${new Date().toISOString().split('T')[0]}.${format}`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  const actionOptions = [
    { value: '', label: 'Todas' },
    { value: 'organization.created', label: 'Org Criada' },
    { value: 'organization.updated', label: 'Org Atualizada' },
    { value: 'organization.deleted', label: 'Org Excluída' },
    { value: 'subscription.created', label: 'Assinatura Criada' },
    { value: 'subscription.updated', label: 'Assinatura Atualizada' },
    { value: 'subscription.cancelled', label: 'Assinatura Cancelada' },
    { value: 'user.blocked', label: 'Usuário Bloqueado' },
    { value: 'user.unblocked', label: 'Usuário Desbloqueado' },
    { value: 'user.impersonated', label: 'Impersonação' },
    { value: 'plan.created', label: 'Plano Criado' },
    { value: 'plan.updated', label: 'Plano Atualizado' },
    { value: 'plan.deleted', label: 'Plano Excluído' },
    { value: 'alert.created', label: 'Alerta Criado' },
    { value: 'alert.resolved', label: 'Alerta Resolvido' },
    { value: 'config.updated', label: 'Config Atualizada' },
    { value: 'payment.refunded', label: 'Pagamento Reembolsado' },
  ]

  const targetTypeOptions = [
    { value: '', label: 'Todos' },
    { value: 'organization', label: 'Organização' },
    { value: 'user', label: 'Usuário' },
    { value: 'subscription', label: 'Assinatura' },
    { value: 'plan', label: 'Plano' },
    { value: 'alert', label: 'Alerta' },
    { value: 'config', label: 'Configuração' },
    { value: 'payment', label: 'Pagamento' },
  ]

  return (
    <div className="flex flex-col h-full">
      <SuperAdminHeader
        title="Logs de Auditoria"
        subtitle="Histórico de todas as ações realizadas na plataforma"
        onMenuToggle={onMenuToggle}
        actions={
          <div className="flex gap-2">
            <button
              onClick={() => handleExport('csv')}
              disabled={exportMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exportar CSV</span>
            </button>
            <button
              onClick={() => handleExport('json')}
              disabled={exportMutation.isPending}
              className="flex items-center gap-2 px-3 py-2 text-sm border border-gray-200 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              <Download className="w-4 h-4" />
              <span className="hidden sm:inline">Exportar JSON</span>
            </button>
          </div>
        }
      />
      <div className="flex-1 overflow-auto p-4 md:p-6 max-w-7xl mx-auto w-full">

      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4 mb-6">
        <div className="flex flex-wrap items-center gap-4">
          <div>
            <label className="block text-xs text-gray-500 mb-1">Ação</label>
            <select
              value={actionFilter}
              onChange={(e) => {
                setActionFilter(e.target.value)
                setPage(1)
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
            >
              {actionOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Tipo de Alvo</label>
            <select
              value={targetTypeFilter}
              onChange={(e) => {
                setTargetTypeFilter(e.target.value)
                setPage(1)
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
            >
              {targetTypeOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Data Inicial</label>
            <input
              type="date"
              value={dateRange.start || ''}
              onChange={(e) => {
                setDateRange((prev) => ({ ...prev, start: e.target.value }))
                setPage(1)
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <div>
            <label className="block text-xs text-gray-500 mb-1">Data Final</label>
            <input
              type="date"
              value={dateRange.end || ''}
              onChange={(e) => {
                setDateRange((prev) => ({ ...prev, end: e.target.value }))
                setPage(1)
              }}
              className="px-3 py-2 border border-gray-200 rounded-lg text-sm focus:ring-2 focus:ring-teal-500"
            />
          </div>
          {(actionFilter || targetTypeFilter || dateRange.start || dateRange.end) && (
            <button
              onClick={() => {
                setActionFilter('')
                setTargetTypeFilter('')
                setDateRange({})
                setPage(1)
              }}
              className="mt-5 px-3 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              Limpar Filtros
            </button>
          )}
        </div>
      </div>

      {/* Audit Log Table */}
      <AuditLogTable logs={logsData?.data || []} loading={logsLoading} />

      {/* Pagination */}
      {logsData && logsData.pagination.total_pages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Mostrando página {logsData.pagination.page} de {logsData.pagination.total_pages}
            {' '}({logsData.pagination.total} registros)
          </p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" />
            </button>
            <button
              onClick={() => setPage((p) => Math.min(logsData.pagination.total_pages, p + 1))}
              disabled={page >= logsData.pagination.total_pages}
              className="p-2 border border-gray-200 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
      </div>
    </div>
  )
}
